import React from "react";

// Article 6 — Psoriasis in Pregnancy: Is the Disease More Dangerous—or the Treatment?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

export function PsoriasisPregnancyTreatmentBiologics() {
  return (
    <article className="mt-10">
      <P>
        Neither has one blanket answer. Mild, skin-limited psoriasis can usually be managed with low-exposure treatment, while severe, unstable or pustular disease may make stopping effective therapy more dangerous than continuing a carefully selected medicine.
      </P>
      <P>
        The useful question is: How active is the disease, which treatment is being used, when does fetal exposure occur, and what may happen if control is lost?
      </P>

      <H2>Pregnancy Can Improve Psoriasis—or Trigger a Flare</H2>
      <P>
        Psoriasis is an immune-mediated inflammatory disease. It is not contagious and cannot pass to the baby through touch. Some women improve during pregnancy, some remain unchanged and some worsen. Flares commonly occur after delivery, and one pregnancy does not reliably predict the next. Assessment should include the type and extent of psoriasis, sleep disturbance, infection, psoriatic arthritis, previous severe flares and the treatment that controlled disease before conception. A few stable plaques and rapidly spreading inflammatory psoriasis are not the same pregnancy problem.
      </P>

      <H2>Does Psoriasis Itself Affect Pregnancy?</H2>
      <P>
        Mild, well-controlled psoriasis does not mean that the baby is infected or will necessarily develop psoriasis, and it does not automatically require repeated specialist scans. Observational studies report modestly higher rates of hypertensive disorders, gestational diabetes, preterm birth and low birth weight among women with psoriasis. The association may be stronger with severe disease and may also reflect obesity, smoking, diabetes or other inflammatory illness. These studies cannot prove that psoriasis alone caused every complication. <Cite n="1" /> The response is not panic. It is to control important disease, identify associated risks and individualise obstetric surveillance rather than treating every plaque as evidence of placental disease.
      </P>

      <H2>Pregnancy Does Not Mean “No Treatment”</H2>
      <P>
        For limited disease, moisturisers and carefully selected topical corticosteroids are commonly used first. Potent steroids should not be repeatedly applied over large areas without supervision. For more extensive psoriasis, narrow-band ultraviolet B— NB-UVB —is generally the preferred phototherapy. Folic-acid adequacy and avoidance of overheating should be considered. PUVA, which combines psoralen medicine with UVA light, is not the routine pregnancy option. <Cite n="2" />
      </P>
      <P>
        When disease is severe, ciclosporin or a selected biologic may be safer than uncontrolled inflammation, poor intake, infection or repeated hospitalisation. The safest plan is not always the one with the fewest medicines; it is the one with the best overall maternal–fetal balance.
      </P>

      <H2>Some Medicines Need Preconception Action</H2>
      <P>
        Methotrexate and acitretin must not be used during pregnancy. Topical tazarotene should also be avoided. The interval between stopping treatment and attempting conception differs by medicine and current product guidance; after acitretin, it is prolonged and must be planned before pregnancy. <Cite n="2" />
      </P>
      <P>
        Newer oral medicines, including apremilast, have insufficient pregnancy safety data and are generally avoided. Accidental exposure is not proof that the baby has been harmed and is not an automatic reason to end the pregnancy. The medicine, dose, last exposure and gestational timing should be reviewed promptly by dermatology and obstetric teams.
      </P>

      <H2>Biologics Are Not Interchangeable</H2>
      <P>
        Most pregnancy experience relates to tumour-necrosis-factor inhibitors. Placental transfer of many antibody biologics rises during the second and especially the third trimester because their Fc portion is actively transported across the placenta. Certolizumab pegol lacks that Fc portion, so placental transfer is low or negligible. British guidance therefore considers it when a biologic must be started around conception or during pregnancy. Continuing, stopping or changing another biologic depends on disease severity, previous relapse, the individual drug and gestational timing. <Cite n="3" />
      </P>
      <P>
        A large 2026 psoriasis cohort found no increase in overall adverse pregnancy outcomes among biologic-exposed pregnancies compared with psoriasis pregnancies without biologics. This is reassuring, but the study was retrospective and could not establish equal safety for every biologic or trimester. <Cite n="4" />
      </P>

      <H2>Plan the Baby’s Live Vaccines Before Delivery</H2>
      <P>
        When a placenta-crossing biologic is continued beyond mid-pregnancy, it may remain in the baby after birth. British guidance generally advises avoiding live vaccines for the first six months after exposure beyond 16 weeks, while checking the individual drug guidance. <Cite n="3" />
      </P>
      <P>
        This matters in India because BCG and oral polio are scheduled at birth and rotavirus begins in early infancy. The exact biologic and last pregnancy dose should be recorded before delivery so the paediatrician can plan vaccination correctly. <Cite n="5" /> Advice may differ after certolizumab because fetal transfer is minimal, but this should be confirmed rather than assumed.
      </P>

      <H2>Seek Urgent Assessment</H2>
      <P>
        Widespread painful pustules, rapidly spreading redness, fever, chills, weakness, dehydration or feeling seriously unwell may represent generalised pustular psoriasis of pregnancy or erythrodermic psoriasis—not an ordinary plaque flare. These conditions require urgent hospital-based dermatology and obstetric assessment. <Cite n="6" />
      </P>
      <P>Also seek maternity assessment for reduced fetal movements or any concerning pregnancy symptom.</P>

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed">
          <strong>Dr Tania’s takeaway:</strong> Pregnancy does not force a choice between treating psoriasis and protecting the baby. Mild disease usually needs local treatment; severe disease may need phototherapy, ciclosporin or a carefully selected biologic. The safest decision matches disease severity, medicine, gestational timing and relapse risk—and writes the newborn vaccine plan before birth.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Rahmati S, Moameri H, Malek Mohammadi N, et al. Impact of maternal psoriasis on adverse maternal and neonatal outcomes: a systematic review and meta-analysis. <em>BMC Pregnancy and Childbirth</em>. 2023;23:703. doi:10.1186/s12884-023-06006-5.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10543305/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pmc.ncbi.nlm.nih.gov/articles/PMC10543305/</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] Hutchison E, Eraifej N, Moss J, Rolls S, Wainman H. A guide to prescribing systemic treatments for psoriasis during pregnancy, breastfeeding and in those trying to conceive: what does the current evidence suggest? <em>Clinical and Experimental Dermatology</em>. 2024;49:1316–1329. doi:10.1093/ced/llae209.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pubmed.ncbi.nlm.nih.gov/38769610/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pubmed.ncbi.nlm.nih.gov/38769610/</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] Smith CH, Yiu ZZN, Bale T, et al. British Association of Dermatologists guidelines for biologic therapy for psoriasis 2020: a rapid update. <em>British Journal of Dermatology</em>. 2020;183:628–637. doi:10.1111/bjd.19039.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://academic.oup.com/bjd/article/183/4/628/6761584"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://academic.oup.com/bjd/article/183/4/628/6761584</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] Bar D, Pavlotsky F, Barzilai A, Yinon Y, Baum M. Pregnancy outcomes in patients with biologic-exposed psoriasis: a large cohort study of real-world safety. <em>Journal of the American Academy of Dermatology</em>. Published online June 5, 2026. doi:10.1016/j.jaad.2026.06.006.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pubmed.ncbi.nlm.nih.gov/42250726/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pubmed.ncbi.nlm.nih.gov/42250726/</a>
          </p>
        </li>
        <li id="ref-5" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[5] National Health Mission, Ministry of Health and Family Welfare, Government of India. National Immunization Schedule. Current online schedule.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://nhm.gov.in/index1.php?lang=1&level=3&lid=379&sublinkid=947"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://nhm.gov.in/index1.php?lang=1&level=3&lid=379&sublinkid=947</a>
          </p>
        </li>
        <li id="ref-6" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[6] Choon SE, Foley PA, Asawanonda P, et al. Asia-Pacific consensus recommendations on the management of generalized pustular psoriasis. <em>Journal of Dermatology</em>. 2024;51:1579–1595. doi:10.1111/1346-8138.17471.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC11624156/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pmc.ncbi.nlm.nih.gov/articles/PMC11624156/</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific dermatology, obstetric, rheumatology or paediatric advice. Do not start, stop or switch psoriasis treatment because of this page. Seek urgent assessment for widespread painful pustules, fever, rapidly spreading redness, dehydration, reduced fetal movements or feeling seriously unwell.
      </P>
    </article>
  );
}
